import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Redirect } from "react-router-dom";
import Axios from "axios";
import {
  FaBed,
  FaUserGraduate,
  FaSearch,
  FaSave,
  FaArrowLeft,
  FaUser,
  FaCheck,
} from "react-icons/fa";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import COLORS from "../resources/colors/COLORS";
import MdlSelectSingleServiceStudent from "../dialog/MdlSelectSingleServiceStudent";
import SessionManager from "../Utils/SessionManager";
import BackButton from "../ChildComponent/BackButton";
import myToast from "../Utils/MyToast";

const API_BASE = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;
const SERVICE_ID = "SRV002";
const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTH_NAME_TO_NUMBER = {
  Jan: "01",
  Feb: "02",
  Mar: "03",
  Apr: "04",
  May: "05",
  Jun: "06",
  Jul: "07",
  Aug: "08",
  Sep: "09",
  Oct: "10",
  Nov: "11",
  Dec: "12",
};

// Created By Cursor
function parseSessionStartDate(raw) {
  if (!raw) return null;
  const value = String(raw).trim();
  let m = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

// Created By Cursor
function defaultAcademicStart() {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return new Date(year, 3, 1);
}

// Created By Cursor
function resolveSessionStartDate(preferred) {
  const candidates = [
    preferred,
    typeof localStorage !== "undefined" ? localStorage.getItem("START_DATE") : null,
    SessionManager.getCurrentSession()?.[0]?.start_date,
  ];
  for (let i = 0; i < candidates.length; i++) {
    const parsed = parseSessionStartDate(candidates[i]);
    if (parsed) return parsed;
  }
  return defaultAcademicStart();
}

// Created By Cursor
function buildSessionMonths(preferred) {
  const cal = resolveSessionStartDate(preferred);
  const y0 = cal.getFullYear();
  const m0 = cal.getMonth();
  const months = new Array(12);
  for (let i = 0; i < 12; i++) {
    const d = new Date(y0, m0 + i, 1);
    months[i] = `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
  }
  return months;
}

// Created By Cursor
function reverseApplicableMonths(monthList) {
  if (!monthList?.length) return "";
  const out = [];
  for (let i = 0; i < monthList.length; i++) {
    const [monthName, year] = String(monthList[i]).split(" ");
    const month = MONTH_NAME_TO_NUMBER[monthName];
    if (month && year) out.push(`${year}-${month}`);
  }
  return out.join(",");
}

const INITIAL_FORM = {
  blockId: "",
  roomNo: "",
  cautionMoney: "",
  hostelFee: "",
  isMessTaken: "No",
};

export default function AddHostelStudent() {
  const loggedIn = localStorage.getItem("token") !== null;

  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [serviceMonth, setServiceMonth] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [mStudentDetails, setSelectedStudent] = useState(null);
  const [mSessionMonth, setSessionMonth] = useState([]);

  const { blockId, roomNo, cautionMoney, hostelFee, isMessTaken } = form;

  const selectedMonthSet = useMemo(() => new Set(serviceMonth), [serviceMonth]);
  const allMonthsSelected =
    mSessionMonth.length > 0 && serviceMonth.length === mSessionMonth.length;

  const classLabel = useMemo(() => {
    const c = mStudentDetails?.class?.[0];
    if (!c) return "—";
    return [c.class_name, c.section_name].filter(Boolean).join(" · ") || "—";
  }, [mStudentDetails]);

  const rollNo =
    mStudentDetails?.roll_number ||
    mStudentDetails?.class?.[0]?.student_roll_no ||
    "—";

  const getAllSessionMonth = useCallback((sessionStartDate) => {
    setSessionMonth(buildSessionMonths(sessionStartDate));
    setServiceMonth([]);
  }, []);

  const handleClose = useCallback(() => setIsOpen(false), []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    const keyMap = {
      blockId: "blockId",
      roomNo: "roomNo",
      cautionMoney: "cautionMoney",
      hostelFee: "hostelFee",
      isMessTaken: "isMessTaken",
    };
    const key = keyMap[name];
    if (!key) return;
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSelectedStudent = useCallback(
    (selectedStudentId, selectedItem, selectedSessionId, _sessionName, sessionStartDate) => {
      setIsOpen(false);
      setSelectedStudent(selectedItem);
      setStudentId(selectedStudentId);
      setSessionId(selectedSessionId);
      getAllSessionMonth(sessionStartDate);
    },
    [getAllSessionMonth]
  );

  const handleMonthClick = useCallback((month) => {
    setServiceMonth((prev) =>
      prev.includes(month) ? prev.filter((m) => m !== month) : [...prev, month]
    );
  }, []);

  const toggleAllMonths = useCallback(() => {
    setServiceMonth((prev) =>
      prev.length === mSessionMonth.length ? [] : mSessionMonth.slice()
    );
  }, [mSessionMonth]);

  const resetForm = useCallback(() => {
    setForm(INITIAL_FORM);
    setServiceMonth([]);
    setSessionMonth([]);
    setStudentId("");
    setSessionId("");
    setSelectedStudent(null);
  }, []);

  const isValidInput = useCallback(() => {
    const checks = [
      [studentId, "Select a student"],
      [String(roomNo || "").trim(), "Enter room number"],
      [String(cautionMoney || "").trim(), "Enter caution money"],
      [String(hostelFee || "").trim(), "Enter hostel fee"],
      [serviceMonth.length, "Select at least one service month"],
    ];
    for (let i = 0; i < checks.length; i++) {
      if (!checks[i][0]) {
        myToast.notify_warning(checks[i][1]);
        return false;
      }
    }
    return true;
  }, [studentId, roomNo, cautionMoney, hostelFee, serviceMonth.length]);

  const handleSubmit = useCallback(async () => {
    if (!isValidInput()) return;

    const schoolCode = SessionManager.getSchoolCode();
    const staffId = SessionManager.getStaffDetails()?.staff_id;
    if (!schoolCode || !staffId) {
      myToast.notify_warning("Staff or school details missing");
      return;
    }

    setSaving(true);
    try {
      const cls = mStudentDetails?.class?.[0];
      const params = new URLSearchParams({
        staff_id: staffId,
        student_id: studentId,
        session_id: sessionId,
        class_id: cls?.class_id || "",
        section_id: cls?.section_id || "",
        block_id: String(blockId || "").trim(),
        room_no: String(roomNo || "").trim(),
        caution_money: String(cautionMoney || "").trim(),
        hostel_fee: String(hostelFee || "").trim(),
        is_mess_taken: isMessTaken,
        applicable_month: reverseApplicableMonths(serviceMonth),
        service_id: SERVICE_ID,
        school_code: schoolCode,
      });

      const response = await Axios.post(
        `${API_BASE}AddHostelStudent.php`,
        params,
        {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        }
      );

      if (response.data?.api_status === "OK") {
        myToast.notify_success("Hostel student added successfully!");
        resetForm();
      } else {
        myToast.notify_warning(
          response.data?.api_remark || "Failed to add hostel student."
        );
      }
    } catch (error) {
      console.error("Error adding hostel student:", error);
      myToast.notify_error("Failed to add hostel student");
    } finally {
      setSaving(false);
    }
  }, [
    isValidInput,
    studentId,
    sessionId,
    mStudentDetails,
    blockId,
    roomNo,
    cautionMoney,
    hostelFee,
    isMessTaken,
    serviceMonth,
    resetForm,
  ]);

  useEffect(() => {
    const start = localStorage.getItem("START_DATE");
    if (start) getAllSessionMonth(start);
  }, [getAllSessionMonth]);

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page sr-page ats-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Add Hostel Student</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaBed />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Add Hostel Student</h1>
              <p className="sm-hero__sub">
                Assign room, caution money, fee, and service months
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <Link to="/AdminMain/HostelStudent" className="ats-hero-link">
              <FaArrowLeft aria-hidden /> Students
            </Link>
          </div>
        </header>

        <div className="sr-body ats-body">
          <section className="sr-section ats-section ats-section--student">
            <div className="sr-section__head">
              <div className="sr-section__icon" aria-hidden>
                <FaUserGraduate />
              </div>
              <div className="min-w-0">
                <h2 className="sr-section__title">Student</h2>
                <p className="sr-section__sub">
                  {mStudentDetails
                    ? "Selected student details"
                    : "Select a student to continue"}
                </p>
              </div>
              <button
                type="button"
                className="sr-btn sr-btn--primary ats-select-btn"
                onClick={() => setIsOpen(true)}
              >
                <FaSearch aria-hidden />
                {mStudentDetails ? "Change Student" : "Select Student"}
              </button>
            </div>
            <div className="sr-section__body">
              {mStudentDetails ? (
                <div className="ats-student">
                  <div className="ats-student__avatar" aria-hidden>
                    {mStudentDetails.student_img_path ? (
                      <img
                        src={mStudentDetails.student_img_path}
                        alt=""
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <FaUser />
                    )}
                  </div>
                  <div className="ats-student__info min-w-0">
                    <h3 className="ats-student__name">
                      {mStudentDetails.stu_name || "Name"}
                      {mStudentDetails.father_name
                        ? ` S/o ${mStudentDetails.father_name}`
                        : ""}
                    </h3>
                    <div className="ats-student__pills">
                      <span className="ats-pill">Roll: {rollNo}</span>
                      <span className="ats-pill">Class: {classLabel}</span>
                      {mStudentDetails.student_enrollment_no ? (
                        <span className="ats-pill">
                          Enroll: {mStudentDetails.student_enrollment_no}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="ats-student-empty">
                  <div className="ats-student-empty__icon" aria-hidden>
                    <FaUserGraduate />
                  </div>
                  <p>No student selected yet</p>
                </div>
              )}
            </div>
          </section>

          <section className="sr-section ats-section ats-section--transport">
            <div className="sr-section__head">
              <div className="sr-section__icon" aria-hidden>
                <FaBed />
              </div>
              <div className="min-w-0">
                <h2 className="sr-section__title">Hostel details</h2>
                <p className="sr-section__sub">Room and caution information</p>
              </div>
            </div>
            <div className="sr-section__body">
              <div className="sr-grid sr-grid--2">
                <div className="sr-field">
                  <label className="sr-field__label" htmlFor="ahs-block">
                    Block
                  </label>
                  <input
                    id="ahs-block"
                    name="blockId"
                    className="form-control sr-input"
                    value={blockId}
                    onChange={handleChange}
                    placeholder="Block / wing (optional)"
                  />
                </div>
                <div className="sr-field">
                  <label className="sr-field__label" htmlFor="ahs-room">
                    Room number <span className="sr-field__req">*</span>
                  </label>
                  <input
                    id="ahs-room"
                    name="roomNo"
                    className="form-control sr-input"
                    value={roomNo}
                    onChange={handleChange}
                    placeholder="e.g. 101"
                  />
                </div>
                <div className="sr-field">
                  <label className="sr-field__label" htmlFor="ahs-caution">
                    Caution money <span className="sr-field__req">*</span>
                  </label>
                  <div className="ats-fee-input-wrap">
                    <span className="ats-fee-prefix" aria-hidden>
                      ₹
                    </span>
                    <input
                      id="ahs-caution"
                      name="cautionMoney"
                      type="number"
                      min="0"
                      className="form-control sr-input ats-fee-input"
                      value={cautionMoney}
                      onChange={handleChange}
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="sr-field">
                  <label className="sr-field__label" htmlFor="ahs-mess">
                    Mess taken
                  </label>
                  <select
                    id="ahs-mess"
                    name="isMessTaken"
                    className="form-control sr-input"
                    value={isMessTaken}
                    onChange={handleChange}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
              </div>
            </div>
          </section>

          <section className="sr-section ats-section ats-section--fee ats-fee-section">
            <div className="sr-section__head">
              <div className="sr-section__icon" aria-hidden>
                <i className="fa fa-inr" />
              </div>
              <div className="min-w-0">
                <h2 className="sr-section__title">Hostel fee & service months</h2>
                <p className="sr-section__sub">
                  {mSessionMonth.length
                    ? `${serviceMonth.length} of ${mSessionMonth.length} months selected`
                    : "Set fee and choose applicable service months"}
                </p>
              </div>
            </div>
            <div className="sr-section__body">
              <div className="ats-fee-months">
                <div className="ats-fee-box">
                  <label className="sr-field__label" htmlFor="ahs-fee">
                    Hostel fee <span className="sr-field__req">*</span>
                  </label>
                  <div className="ats-fee-input-wrap">
                    <span className="ats-fee-prefix" aria-hidden>
                      ₹
                    </span>
                    <input
                      id="ahs-fee"
                      name="hostelFee"
                      type="number"
                      min="0"
                      className="form-control sr-input ats-fee-input"
                      value={hostelFee}
                      onChange={handleChange}
                      placeholder="0"
                    />
                  </div>
                  <p className="sr-field__hint">Monthly hostel fee amount</p>
                </div>

                <div className="ats-months-box">
                  <div className="ats-months-box__head">
                    <span className="sr-field__label">
                      Service months <span className="sr-field__req">*</span>
                    </span>
                    <button
                      type="button"
                      className="ats-months-toggle"
                      onClick={toggleAllMonths}
                      disabled={!mSessionMonth.length}
                    >
                      {allMonthsSelected ? "Clear all" : "Select all"}
                    </button>
                  </div>
                  <div className="ats-months-grid">
                    {mSessionMonth.map((month) => {
                      const selected = selectedMonthSet.has(month);
                      return (
                        <button
                          key={month}
                          type="button"
                          className={`ats-month-pill${
                            selected ? " is-selected" : ""
                          }`}
                          onClick={() => handleMonthClick(month)}
                          aria-pressed={selected}
                        >
                          {selected ? <FaCheck aria-hidden /> : null}
                          {month}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div className="ats-actions">
            <Link
              to="/AdminMain/HostelStudent"
              className="sr-btn sr-btn--ghost ats-btn-cancel"
            >
              Cancel
            </Link>
            <button
              type="button"
              className="sr-btn sr-btn--primary ats-btn-submit"
              onClick={handleSubmit}
              disabled={saving}
            >
              <FaSave aria-hidden />
              {saving ? "Saving…" : "Submit Hostel Details"}
            </button>
          </div>
        </div>
      </div>

      {isOpen ? (
        <MdlSelectSingleServiceStudent
          actionClose={handleClose}
          actionDone={handleSelectedStudent}
          serviceId={SERVICE_ID}
        />
      ) : null}
    </div>
  );
}
